export type FiltresInterventions = { debut?: string; fin?: string };

type InterventionBase = {
  id: number;
  /** Type métier fourni par l’API, indépendant de la date et de la résolution du ticket. */
  nature?: 'planification' | 'bon';
  date_intervention: string;
  /** Date de saisie ; absente des anciennes réponses, null si inconnue. */
  dateCreation?: string | null;
  date_deb: string | null;
  date_fin: string | null;
  intervenant: string;
  commentaire: string | null;
  pb_resolu: boolean | null;
  ticket: {
    id: number;
    numero: string;
    date_ouverture?: string | null;
    libelle?: string | null;
    installation?: { id: number; nom?: string } | null;
  } | null;
};

export type InterventionCurative = InterventionBase & { type: 'intervention_curative' };
export type InterventionPreventive = InterventionBase & {
  type: 'intervention_preventive';
  remplacement: number | null;
  serial_remplace: string | null;
  idIntervenant: number | null;
};
export type InterventionBrute = InterventionCurative | InterventionPreventive;

export type InterventionsResponse = {
  filters: { debut: string | null; fin: string | null };
  'interventions-curatives': InterventionCurative[];
  'interventions-preventives': InterventionPreventive[];
};
export type ParcInterventionsResponse = InterventionsResponse & { parc: number };
export type InstallationInterventionsResponse = InterventionsResponse & { installation: number };

export function reunirInterventions(response: InterventionsResponse | null): InterventionBrute[] {
  return [
    ...(response?.['interventions-curatives'] ?? []),
    ...(response?.['interventions-preventives'] ?? []),
  ];
}

export type CompteursInterventions = { planifiees: number; cloturees: number };

/** Les interventions résolues sont clôturées ; seules les non-résolues datées d'aujourd'hui ou après sont planifiées. */
export function compterInterventions(
  response: InterventionsResponse,
  maintenant = new Date(),
): CompteursInterventions {
  const aujourdHui = `${maintenant.getFullYear()}-${String(maintenant.getMonth() + 1).padStart(2, '0')}-${String(maintenant.getDate()).padStart(2, '0')}`;
  const compteurs: CompteursInterventions = { planifiees: 0, cloturees: 0 };
  for (const intervention of reunirInterventions(response)) {
    if (intervention.pb_resolu === true) {
      ++compteurs.cloturees;
    } else {
      const date = (intervention.date_deb ?? intervention.date_intervention)?.slice(0, 10);
      if (
        date &&
        /^\d{4}-\d{2}-\d{2}$/.test(date) &&
        dateCalendaireValide(date) &&
        date >= aujourdHui
      )
        ++compteurs.planifiees;
    }
  }
  return compteurs;
}

/** Bornes inclusives du mois, en dates locales sans conversion UTC. */
export function filtresInterventionsMois(date: Date): FiltresInterventions {
  const mois = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  const dernierJour = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  return { debut: `${mois}-01`, fin: `${mois}-${dernierJour}` };
}

export function libelleTypeIntervention(type: InterventionBrute['type']): string {
  return type === 'intervention_preventive' ? 'Préventive' : 'Curative';
}

/** Date UTC utilisée uniquement pour valider le calendrier, sans déplacer le jour de l'API. */
function dateCalendaireValide(jour: string): boolean {
  const date = new Date(jour + 'T00:00:00Z');
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === jour;
}
